/**
 * Org-namespaced persistence for Zustand's `persist` middleware.
 *
 * A `persist({ name: 'metadata-store' })` writes one localStorage entry for the
 * whole browser profile. When the persisted slice contains org-specific things —
 * open editor tabs, a selected record, a retrieval id — that entry is a leak
 * that survives not just an org switch but a full page reload: Org B rehydrates
 * Org A's tabs before it has fetched anything of its own.
 *
 * This storage adapter redirects every read and write to a key namespaced by the
 * organization that is active at that moment:
 *
 *     org:{organizationId}:{name}
 *
 * so the same store transparently keeps one persisted document per org, and
 * reads nothing at all while no org is selected (rather than reading a shared
 * one). Pair it with `registerOrgScopedState` so the in-memory copy is re-read
 * on a switch — persistence namespacing alone fixes reloads, not switches.
 */

import type { PersistStorage, StorageValue } from 'zustand/middleware';

import { getActiveOrgId } from './orgScope';
import type { OrgId } from './types';

/** The localStorage/sessionStorage key an org's document lives under. */
export function orgScopedKey(name: string, orgId: OrgId | null): string | null {
  return orgId ? `org:${orgId}:${name}` : null;
}

/**
 * Build a `persist`-compatible storage that namespaces by the active organization.
 *
 * @param name  Logical document name, e.g. `'metadata-store'`.
 * @param backing  Storage to write through to. Defaults to `localStorage`;
 *                 injectable so tests don't need a DOM.
 */
export function createOrgScopedJSONStorage<S>(
  name: string,
  backing: () => Storage | undefined = defaultBackingStorage,
): PersistStorage<S> {
  const resolve = (): { store: Storage; key: string } | null => {
    const store = backing();
    if (!store) return null;
    const key = orgScopedKey(name, getActiveOrgId());
    return key ? { store, key } : null;
  };

  return {
    getItem: (): StorageValue<S> | null => {
      const target = resolve();
      if (!target) return null;
      try {
        const raw = target.store.getItem(target.key);
        return raw ? (JSON.parse(raw) as StorageValue<S>) : null;
      } catch {
        // Corrupt document: behave as if nothing was persisted rather than
        // wedging the store on every load.
        return null;
      }
    },

    setItem: (_key, value): void => {
      const target = resolve();
      // No organization selected means this state has no owner yet. Dropping the
      // write is the point: it is what stops unowned state becoming shared state.
      if (!target) return;
      try {
        target.store.setItem(target.key, JSON.stringify(value));
      } catch {
        /* quota or private-mode: persistence is an optimisation, never required */
      }
    },

    removeItem: (): void => {
      const target = resolve();
      if (!target) return;
      try {
        target.store.removeItem(target.key);
      } catch {
        /* nothing actionable */
      }
    },
  };
}

function defaultBackingStorage(): Storage | undefined {
  return typeof localStorage === 'undefined' ? undefined : localStorage;
}

/**
 * Delete a legacy, un-namespaced persisted document.
 *
 * Called once at module load by stores that used to persist globally, so an
 * existing tab does not keep rehydrating one org's state into every org. There
 * is deliberately no migration into the namespaced key: we cannot know which
 * organization the old document belonged to, and guessing is how the bug got
 * here.
 */
export function dropLegacyGlobalPersistedState(legacyKey: string): void {
  // `typeof` rather than `localStorage?.` — a bare reference to an undeclared
  // global throws a ReferenceError rather than yielding undefined, which would
  // make importing any store that calls this fail outright under a non-DOM
  // runtime (the test environment, SSR).
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(legacyKey);
  } catch {
    /* nothing actionable */
  }
}
